/**
 * EquipmentType API client.
 * STORY-1306 (Sprint 17 W3) — connects to /api/v1/equipment-types endpoints
 * mounted by STORY-1306a-prereq (apps/kgc-api/src/modules/equipment-types/).
 *
 * S17W4-C 2026-05-14: TODO closed — zod schemas + types imported from
 * @kgc/equipment-type-domain (frontend-safe sub-package, NO NestJS imports).
 */

export {
  createEquipmentTypeSchema,
  EQUIPMENT_TYPE_CATEGORIES,
  updateEquipmentTypeSchema,
} from '@kgc/equipment-type-domain';
export type {
  CreateEquipmentTypeDto as CreateEquipmentTypeInput,
  EquipmentTypeCategory,
  UpdateEquipmentTypeDto as UpdateEquipmentTypeInput,
} from '@kgc/equipment-type-domain';

import type { EquipmentType as DomainEquipmentType } from '@kgc/equipment-type-domain';

import { api } from './client';

// API response shape — domain type with ISO date strings (JSON over HTTP).
export type EquipmentType = Omit<DomainEquipmentType, 'createdAt' | 'updatedAt'> & {
  createdAt: string;
  updatedAt: string;
};

export interface EquipmentTypeListFilter {
  category?: string;
  isActive?: boolean;
  search?: string;
}

import type { CreateEquipmentTypeDto, UpdateEquipmentTypeDto } from '@kgc/equipment-type-domain';

export async function listEquipmentTypes(filter: EquipmentTypeListFilter = {}) {
  const params = new URLSearchParams();
  if (filter.category !== undefined) params.set('category', filter.category);
  if (filter.isActive !== undefined) params.set('isActive', String(filter.isActive));
  if (filter.search !== undefined && filter.search.length > 0) params.set('search', filter.search);
  const qs = params.toString();
  const path = qs.length > 0 ? `/equipment-types?${qs}` : '/equipment-types';
  return api.get<{ data: EquipmentType[] }>(path);
}

export async function getEquipmentType(id: string) {
  return api.get<{ data: EquipmentType }>(`/equipment-types/${id}`);
}

export async function createEquipmentType(input: CreateEquipmentTypeDto) {
  return api.post<{ data: EquipmentType }>('/equipment-types', input);
}

export async function updateEquipmentType(id: string, input: UpdateEquipmentTypeDto) {
  return api.patch<{ data: EquipmentType }>(`/equipment-types/${id}`, input);
}

// ─── STORY-1306 slice C: webshop-tab seam (pinnelt kontraktus, #407) ──────────

import {
  equipmentTypeWebshopPatchSchema,
  equipmentTypeWebshopReadSchema,
  type EquipmentTypeWebshopPatch,
  type EquipmentTypeWebshopRead,
} from '@kgc/equipment-type-domain';

export type { EquipmentTypeWebshopPatch, EquipmentTypeWebshopRead };

/**
 * A webshop-fragment a meglévő GET /equipment-types/:id válaszából — a pinnelt
 * read-schemával RUNTIME-validálva (nem-strict objektum: a többi mező átmegy,
 * a seam-drift azonnal kiderül).
 */
export async function getEquipmentTypeWebshop(id: string): Promise<EquipmentTypeWebshopRead> {
  const raw = await api.get<{ data: unknown }>(`/equipment-types/${id}`);
  return equipmentTypeWebshopReadSchema.parse(raw.data);
}

/** PATCH a meglévő update-endpointtal — client-side kontraktus-validálással. */
export async function patchEquipmentTypeWebshop(
  id: string,
  patch: EquipmentTypeWebshopPatch
): Promise<EquipmentTypeWebshopRead> {
  const validated = equipmentTypeWebshopPatchSchema.parse(patch);
  const raw = await api.patch<{ data: unknown }>(`/equipment-types/${id}`, validated);
  return equipmentTypeWebshopReadSchema.parse(raw.data);
}

// ─── STORY-1306 slice D: docs-tab seam (Document-reuse, read-only — #407) ────

import {
  equipmentTypeDocumentsReadSchema,
  type EquipmentTypeDocument,
} from '@kgc/equipment-type-domain';

export type { EquipmentTypeDocument };

/** A típushoz tartozó dokumentumok (Document-reuse, nincs dedikált tábla). */
export async function listEquipmentTypeDocuments(id: string): Promise<EquipmentTypeDocument[]> {
  const raw = await api.get<unknown>(`/equipment-types/${id}/documents`);
  return equipmentTypeDocumentsReadSchema.parse(raw).data;
}
